import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Slide, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import globals from '../globals';
import RedButton from '../components/redButton';
import BlueButton from '../components/blueButton';
import { useAuth } from '../context/AuthContext';
import NotFound from '../error-page';
import LoadingPage from '../error_page/loadingPage';
import ExamLanding, { CheckList, formatPrice, mutedText } from './ExamLanding';
import { buildExamContent } from './examContent';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const ExamPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, token } = useAuth();

    const [exam, setExam] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(false);

    useEffect(() => {
        fetch(`${globals.apiUrl}/exams/${id}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        })
            .then(res => { if (!res.ok) throw new Error(); return res.json(); })
            .then(data => { setExam(data); setIsLoading(false); })
            .catch(() => { setHasError(true); setIsLoading(false); });
    }, [id, token]);

    useEffect(() => {
        if (!isLoading && window.location.hash) {
            const el = document.getElementById(window.location.hash.slice(1));
            if (el) el.scrollIntoView({ behavior: 'smooth' });
        }
    }, [isLoading]);

    const content = useMemo(() => (exam ? buildExamContent(exam) : null), [exam]);

    const handleBuy = () => {
        if (!user) {
            navigate(`/login?redirect=/exams/${id}`);
            return;
        }
        setOpenDialog(true);
    };

    const handleCheckout = () => {
        setCheckoutLoading(true);
        fetch(`${globals.apiUrl}/exams/${id}/create-checkout`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ currency: 'EUR' }),
        })
            .then(res => res.json())
            .then(data => {
                if (data.url) window.location.href = data.url;
                else setCheckoutLoading(false);
            })
            .catch(() => setCheckoutLoading(false));
    };

    if (isLoading) {
        return <LoadingPage />;
    }

    if (hasError || !exam) {
        return <NotFound />;
    }

    const price = formatPrice(exam.price);

    return (
        <>
            <Helmet>
                <title>{content.metaTitle}</title>
                <meta name="description" content={content.metaDescription} />
                <meta property="og:title" content={content.metaTitle} />
                <meta property="og:description" content={content.metaDescription} />
            </Helmet>

            <ExamLanding exam={exam} onBuy={handleBuy} showRegistrationNote={!user} hideSticky={openDialog} />

            {/* Purchase dialog */}
            <Dialog
                open={openDialog}
                TransitionComponent={Transition}
                onClose={() => setOpenDialog(false)}
                maxWidth="xs"
                fullWidth
                aria-labelledby="purchase-dialog-title"
            >
                <DialogTitle id="purchase-dialog-title" sx={{ pr: 6 }}>
                    {content.purchaseDialogTitle}
                    <IconButton aria-label="Затвори" onClick={() => setOpenDialog(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ textAlign: 'center' }}>
                        <img src="/popup_stripe.webp" alt="" width={120} height={136} />
                    </Box>
                    <Typography sx={{ mt: 1, color: mutedText }}>{content.purchaseDialogText}</Typography>
                    {content.purchaseHighlights.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <CheckList items={content.purchaseHighlights} iconSize={20} />
                        </Box>
                    )}
                    <Typography sx={{ mt: 1, fontWeight: 500 }}>Цена: {price}</Typography>
                </DialogContent>
                <Divider />
                <DialogActions>
                    <BlueButton onClick={() => setOpenDialog(false)} size="medium" text="Откажи" sx={{ fontWeight: 'bold' }} />
                    <RedButton
                        onClick={handleCheckout}
                        loading={checkoutLoading}
                        size="medium"
                        text="Продължи към покупка"
                        sx={{ fontWeight: 'bold' }}
                    />
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ExamPage;
